/**
 * Client-side mirror of QuestionnaireQuestionRule#matches? and
 * QuestionnaireResponse progression. Used by the admin builder preview to
 * test-drive logic jumps, and reusable by the member-facing flow.
 *
 * Question shape: { ref, rules: [...] } where ref is a stable client id.
 * Rule shape: { operator, values, action, subject, userAttribute, answerDataKey,
 * destinationRef, stopperHeading, stopperBody }
 */

// Fixed list of user attributes a rule can compare against instead of the
// question's answer. Mirrors QuestionnaireQuestionRule::USER_ATTRIBUTES —
// extend both together.
export const USER_ATTRIBUTE_OPTIONS = [
  { value: 'zipcode', label: "user's zip code" },
  { value: 'country_code', label: "user's country" },
  { value: 'language_code', label: "user's language" },
];

export const AGE_OPERATORS = ['age_in_months_less_than', 'age_in_months_at_least'];

// Builds the user-attribute lookup evaluateAnswer expects from a real user
// record (e.g. context.auth.getCurrentUser()), so the member-facing flow can
// mirror server-side rule matching for user_attribute rules.
export const userAttributesFromUser = (user) => ({
  zipcode: user ? user.zipcode : undefined,
  country_code: user ? user.country_code : undefined,
  language_code: user ? user.language_code : undefined,
});

// Whole months between `value` (a date string) and today, mirroring
// QuestionnaireQuestionRule#age_matches?. Returns null when the value is not
// a parseable date, which never matches.
const ageInMonthsFrom = (value, today = new Date()) => {
  if (!value) return null;
  // Ensure YYYY-MM-DD strings are parsed in local time, not UTC, to avoid
  // off-by-one issues around midnight in some timezones. Other formats fall
  // back to native Date parsing.
  let date;
  const ymdMatch = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(String(value));
  if (ymdMatch) {
    const year = Number(ymdMatch[1]);
    const monthIndex = Number(ymdMatch[2]) - 1; // 0-based
    const day = Number(ymdMatch[3]);
    date = new Date(year, monthIndex, day);
  } else {
    date = new Date(value);
  }
  if (Number.isNaN(date.getTime())) return null;

  let months = (today.getFullYear() - date.getFullYear()) * 12 + (today.getMonth() - date.getMonth());
  if (today.getDate() < date.getDate()) months -= 1;
  return months;
};

export const ruleMatches = (rule, answer, userAttributes = {}, answers = {}) => {
  let compared = answer;
  if (rule.subject === 'user_attribute') compared = userAttributes[rule.userAttribute];
  if (rule.subject === 'answer_data_key') compared = answers[rule.answerDataKey];

  const present = Array.isArray(compared)
    ? compared.some((entry) => String(entry).trim() !== '')
    : compared != null && String(compared).trim() !== '';

  if (AGE_OPERATORS.includes(rule.operator)) {
    const months = ageInMonthsFrom(compared);
    if (months === null) return false;
    const threshold = Number((rule.values || [])[0]);
    return rule.operator === 'age_in_months_less_than' ? months < threshold : months >= threshold;
  }

  const values = (rule.values || []).map((value) => String(value).toLowerCase());

  switch (rule.operator) {
    case 'equals':
      return !Array.isArray(compared) && String(compared).toLowerCase() === values[0];
    case 'any_of':
      return !Array.isArray(compared) && values.includes(String(compared).toLowerCase());
    case 'includes': {
      const comparedValues = (Array.isArray(compared) ? compared : [compared]).map((entry) =>
        String(entry).toLowerCase()
      );
      return comparedValues.some((entry) => values.includes(entry));
    }
    case 'exists':
      return present;
    case 'not_exists':
      return !present;
    default:
      return false;
  }
};

/**
 * Determines what happens after answering a question. First matching rule
 * wins; with no match the flow falls through to the next question in order,
 * ending when none remain. `userAttributes` supplies values for rules whose
 * subject is a user attribute rather than the question's answer, and
 * `answers` supplies previously recorded answers (merged with mentor match
 * data) for answer_data_key rules.
 *
 * @returns {{ type: 'jump'|'next'|'end'|'disqualify', nextRef?: string, rule?: Object }}
 */
export const evaluateAnswer = (questions, questionRef, answer, userAttributes = {}, answers = {}) => {
  const index = questions.findIndex((question) => question.ref === questionRef);
  if (index < 0) return { type: 'end', rule: null };

  const question = questions[index];
  const matched = (question.rules || []).find((rule) => ruleMatches(rule, answer, userAttributes, answers));

  if (matched) {
    if (matched.action === 'jump') return { type: 'jump', nextRef: matched.destinationRef, rule: matched };
    if (matched.action === 'end') return { type: 'end', rule: matched };
    if (matched.action === 'disqualify') return { type: 'disqualify', rule: matched };
  }

  const next = questions[index + 1];
  if (next) return { type: 'next', nextRef: next.ref, rule: null };

  return { type: 'end', rule: null };
};
